import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Autocomplete,
  IconButton,
  Chip,
  Typography,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  AttachFile as AttachFileIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ko } from 'date-fns/locale';
import { Mold } from '../../types';
import { HistoryFormData, HistoryType, HistoryFile } from '../../types/history.types';

interface HistoryFormModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: HistoryFormData) => Promise<void>;
  molds: Mold[];
  initialData?: Partial<HistoryFormData> & { files?: HistoryFile[] };
}

const HistoryFormModal: React.FC<HistoryFormModalProps> = ({
  open,
  onClose,
  onSubmit,
  molds,
  initialData,
}) => {
  const [formData, setFormData] = useState<HistoryFormData>({
    moldId: '',
    type: 'other',
    title: '',
    description: '',
    date: new Date(),
  });
  const [loading, setLoading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [existingFiles, setExistingFiles] = useState<HistoryFile[]>([]);
  const [selectedCavities, setSelectedCavities] = useState<number[]>([]);

  useEffect(() => {
    if (open) {
      if (initialData) {
        const { files, ...restData } = initialData;
        setFormData({
          moldId: '',
          type: 'other',
          title: '',
          description: '',
          date: new Date(),
          ...restData
        });
        setExistingFiles(files || []);
        setSelectedCavities((restData as any).selectedCavities || []);
      } else {
        setFormData({
          moldId: '',
          type: 'other',
          title: '',
          description: '',
          date: new Date(),
        });
        setExistingFiles([]);
        setSelectedCavities([]);
      }
      setSelectedFiles([]);
    }
  }, [open, initialData]);

  const handleSubmit = async () => {
    if (!formData.moldId || !formData.type || !formData.title) {
      alert('금형, 유형, 제목은 필수입니다.');
      return;
    }

    // production 타입이고 캐비티가 있는 경우 유효성 검사
    const selectedMoldData = molds.find(m => m.id === formData.moldId);
    if (formData.type === 'production' && selectedMoldData?.cavities && selectedMoldData.cavities.length > 0) {
      if (selectedCavities.length === 0) {
        alert('최소 1개 이상의 패밀리를 선택해주세요.');
        return;
      }
    }

    setLoading(true);
    try {
      // 파일 포함하여 제출 (기존 파일 + 새 파일)
      const dataWithFiles = { 
        ...formData, 
        files: selectedFiles,
        existingFiles, // 기존 파일 정보 전달
        ...(formData.type === 'production' && selectedCavities.length > 0 && { selectedCavities }),
      };
      await onSubmit(dataWithFiles);
      handleClose();
    } catch (error) {
      console.error('이력 등록 실패:', error);
      alert('이력 등록에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      moldId: '',
      type: 'other',
      title: '',
      description: '',
      date: new Date(),
    });
    setSelectedFiles([]);
    setExistingFiles([]);
    setSelectedCavities([]);
    onClose();
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      setSelectedFiles([...selectedFiles, ...Array.from(files)]);
    }
  };

  const handleFileRemove = (index: number) => {
    setSelectedFiles(selectedFiles.filter((_, i) => i !== index));
  };

  const handleExistingFileRemove = (index: number) => {
    setExistingFiles(existingFiles.filter((_, i) => i !== index));
  };

  // 캐비티 선택 핸들러
  const handleCavityToggle = (cavityNumber: number) => {
    setSelectedCavities(prev => {
      if (prev.includes(cavityNumber)) {
        return prev.filter(num => num !== cavityNumber);
      } else {
        return [...prev, cavityNumber];
      }
    });
  };

  const selectedMold = molds.find(m => m.id === formData.moldId);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{initialData ? '이력 수정' : '이력 추가'}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {/* 금형 선택 */}
          <Autocomplete
            options={molds}
            getOptionLabel={(option) => `${option.moldNumber} - ${option.moldName}`}
            value={selectedMold || null}
            onChange={(_, value) => setFormData({ ...formData, moldId: value?.id || '' })}
            renderInput={(params) => (
              <TextField {...params} label="금형 *" placeholder="금형을 선택하세요" />
            )}
          />

          {/* 유형 선택 */}
          <FormControl fullWidth>
            <InputLabel>유형 *</InputLabel>
            <Select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value as HistoryType })}
              label="유형 *"
            >
              <MenuItem value="other">기타</MenuItem>
              <MenuItem value="production">일일생산량</MenuItem>
              <MenuItem value="inspection">점검</MenuItem>
              <MenuItem value="maintenance">정기 정비</MenuItem>
              <MenuItem value="repair">긴급 수리</MenuItem>
              <MenuItem value="modification">개조/개선</MenuItem>
              <MenuItem value="accident">사고/파손</MenuItem>
              <MenuItem value="disposal">폐기</MenuItem>
            </Select>
          </FormControl>

          {/* 날짜 */}
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ko}>
            <DatePicker
              label="날짜 *"
              value={formData.date}
              onChange={(date) => setFormData({ ...formData, date: date || new Date() })}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </LocalizationProvider>

          {/* 제목 */}
          <TextField
            label="제목 *"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            fullWidth
          />

          {/* 설명 */}
          <TextField
            label="설명"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            multiline
            rows={3}
            fullWidth
          />

          {/* 생산 패밀리 선택 */}
          {formData.type === 'production' && selectedMold?.cavities && selectedMold.cavities.length > 0 && (
            <Box
              sx={{
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                사용 패밀리 선택 *
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {selectedMold.cavities.map((cavity) => (
                  <FormControlLabel
                    key={cavity.cavityNumber}
                    control={
                      <Checkbox
                        checked={selectedCavities.includes(cavity.cavityNumber)}
                        onChange={() => handleCavityToggle(cavity.cavityNumber)}
                      />
                    }
                    label={`패밀리 ${cavity.cavityNumber}: ${cavity.productName}`}
                  />
                ))}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                생산에 사용된 패밀리를 선택하세요 (복수 선택 가능)
              </Typography>
            </Box>
          )}

          {/* 점검 필드 */}
          {formData.type === 'inspection' && (
            <>
              <TextField
                label="점검자"
                value={formData.inspector || ''}
                onChange={(e) => setFormData({ ...formData, inspector: e.target.value })}
                fullWidth
              />
              <FormControl fullWidth>
                <InputLabel>점검 결과</InputLabel>
                <Select
                  value={formData.inspectionResult || ''}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    inspectionResult: e.target.value as 'normal' | 'abnormal' 
                  })}
                  label="점검 결과"
                >
                  <MenuItem value="normal">정상</MenuItem>
                  <MenuItem value="abnormal">이상</MenuItem>
                </Select>
              </FormControl>
            </>
          )}

          {/* 수리 필드 */}
          {formData.type === 'repair' && (
            <>
              <TextField
                label="수리 비용"
                type="number"
                value={formData.cost || ''}
                onChange={(e) => setFormData({ ...formData, cost: Number(e.target.value) })}
                fullWidth
              />
              <TextField
                label="수리 업체"
                value={formData.vendor || ''}
                onChange={(e) => setFormData({ ...formData, vendor: e.target.value })}
                fullWidth
              />
            </>
          )}

          {/* 폐기 필드 */}
          {formData.type === 'disposal' && (
            <>
              <TextField
                label="폐기 사유"
                value={formData.disposalReason || ''}
                onChange={(e) => setFormData({ ...formData, disposalReason: e.target.value })}
                multiline
                rows={2}
                fullWidth
              />
              <TextField
                label="폐기 방법"
                value={formData.disposalMethod || ''}
                onChange={(e) => setFormData({ ...formData, disposalMethod: e.target.value })}
                fullWidth
              />
            </>
          )}

          {/* 파일 첨부 */}
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                파일 첨부
              </Typography>
              <input
                accept="image/*,.pdf,.doc,.docx"
                style={{ display: 'none' }}
                id="file-upload"
                type="file"
                multiple
                onChange={handleFileSelect}
              />
              <label htmlFor="file-upload">
                <IconButton component="span" size="small" color="primary">
                  <AttachFileIcon />
                </IconButton>
              </label>
            </Box>
            
            {/* 기존 첨부파일 목록 */}
            {existingFiles.length > 0 && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                  기존 파일
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {existingFiles.map((file, index) => (
                    <Chip
                      key={index}
                      label={file.name}
                      size="small"
                      color="default"
                      onDelete={() => handleExistingFileRemove(index)}
                      deleteIcon={<CloseIcon />}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* 새로 선택된 파일 목록 */}
            {selectedFiles.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                  새 파일
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {selectedFiles.map((file, index) => (
                    <Chip
                      key={index}
                      label={file.name}
                      size="small"
                      color="primary"
                      onDelete={() => handleFileRemove(index)}
                      deleteIcon={<CloseIcon />}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading} sx={{ color: 'white' }}>
          취소
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading} sx={{ color: 'white' }}>
          {loading ? '처리 중...' : '저장'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HistoryFormModal;
